/**
 * Onboarding-documenten — types + Zod-schemas voor PR C4.
 *
 * Bron: spec C §5 (automatische documentgeneratie + ondertekening) + §16.4
 * (edge case "hergeneren = nieuwe DocumentVersion").
 *
 * Per onboarding kan een set documenten worden gegenereerd of geüpload:
 *   - Gegenereerd: docxtemplater rendert een DOCX-template (Document met
 *     scope=BEDRIJFSBREED + categorie=ONBOARDING_TEMPLATE) en LibreOffice
 *     converteert naar PDF. De PDF wordt als Document/DocumentVersion
 *     bewaard via de generieke StorageProvider, en `OnboardingDocument`
 *     verwijst ernaar.
 *   - Handmatig: HR uploadt een PDF (bv. wanneer geen template beschikbaar
 *     is — spec §16.4) → `isGegenereerd: false`.
 *
 * Ondertekening: medewerker opent de PDF in het self-service-portaal en
 * klikt "Ik ga akkoord". We zetten `ondertekendOp` + `ondertekendDoor` en
 * leggen in `OnboardingAudit` IP/UA/token-jti vast (spec §5.3).
 */

import { z } from 'zod'
import { idParam } from './id-validators'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij generatie.
import { BriefpapierKeuzeInvoerSchema } from './briefpapier'

// ─── Enums (matching Prisma `OnboardingDocumentType`) ────────────────────────

export const ONBOARDING_DOCUMENT_TYPE = [
  'ARBEIDSOVEREENKOMST',
  'LOONHEFFINGSKORTING',
  'WELKOMSTBRIEF',
  'HANDBOEK_ONTVANGST',
  'AVG_PRIVACYVERKLARING',
  'BRUIKLEEN_VOERTUIG',
  'BRUIKLEEN_KLEDING',
  'BRUIKLEEN_GEREEDSCHAP',
  'BRUIKLEEN_TELEFOON',
  // Spec 5 Deel 4 + ADDENDA D.9 — extra HR-/onboarding-documenttypen.
  'ARBEIDSOVEREENKOMST_BEPAALDE_TIJD',
  'ARBEIDSOVEREENKOMST_0UREN',
  'OPDRACHTBEVESTIGING',
  'OVEREENKOMST_VAN_OPDRACHT',
  'STUDIEOVEREENKOMST',
  'BEDRIJFSAUTO_PRIVEGEBRUIK',
  'BEDRIJFSAUTO_VERBOD_PRIVE',
  // Batch-7 recruitment (B4) — aanbiedingsbrief voor kandidaten. Registratie-
  // only voor de onboarding-flow (NIET in ONBOARDING_AUTO_GENERATE_TYPES);
  // gerenderd vanuit KandidaatAanbieding via de aanbieding-generator.
  'AANBIEDINGSBRIEF',
  // batch-42 onboarding (FO-370) — voorblad van het aanmeldpakket. Registratie-
  // only (NIET in ONBOARDING_AUTO_GENERATE_TYPES): de aanmeldpakket-export
  // rendert dit template zelf via het werkgeversverklaring-generatiepad.
  'AANMELDFORMULIER',
  // batch-45 onboarding (FO-382) — welkomsbrief-bijlage bij de welkomstmail.
  // Registratie-only (NIET in ONBOARDING_AUTO_GENERATE_TYPES): gerenderd op het
  // welkomstmail-verzendmoment (met de token-link als mergeveld) door de
  // welkomsbrief-service. Apart van het bestaande WELKOMSTBRIEF-type, dat via
  // de reguliere generator zonder token-link rendert.
  'WELKOMSBRIEF_ONBOARDING',
  'OVERIG',
] as const
export type OnboardingDocumentType = (typeof ONBOARDING_DOCUMENT_TYPE)[number]

/**
 * Document-typen die de onboarding "genereer alle documenten"-flow standaard
 * probeert wanneer de caller geen expliciete `types` meegeeft. De granulaire
 * typen die in spec 5 / ADDENDA D.9 zijn toegevoegd
 * (ARBEIDSOVEREENKOMST_BEPAALDE_TIJD, ARBEIDSOVEREENKOMST_0UREN,
 * OPDRACHTBEVESTIGING, OVEREENKOMST_VAN_OPDRACHT, STUDIEOVEREENKOMST,
 * BEDRIJFSAUTO_PRIVEGEBRUIK, BEDRIJFSAUTO_VERBOD_PRIVE) zijn
 * template-registratie-only: ze worden als (concept) ClientDocumentTemplate
 * beheerd c.q. via de bestaande ARBEIDSOVEREENKOMST/BRUIKLEEN_VOERTUIG +
 * variant-discriminator gerenderd, en horen NIET in de auto-genereer-set.
 */
export const ONBOARDING_AUTO_GENERATE_TYPES: readonly OnboardingDocumentType[] = [
  'ARBEIDSOVEREENKOMST',
  'LOONHEFFINGSKORTING',
  'WELKOMSTBRIEF',
  'HANDBOEK_ONTVANGST',
  'AVG_PRIVACYVERKLARING',
  'BRUIKLEEN_VOERTUIG',
  'BRUIKLEEN_KLEDING',
  'BRUIKLEEN_GEREEDSCHAP',
  'BRUIKLEEN_TELEFOON',
  'OVERIG',
] as const

/**
 * Arbeidsovereenkomst-varianten (gelijk aan de Prisma `ContractType`-waarden,
 * zie `@streep/types` `ContractType`). Wordt door de generator als
 * `ClientDocumentTemplate.variant` gematcht voor `ARBEIDSOVEREENKOMST`-templates.
 * Alleen {BEPAALDE_TIJD, ONBEPAALDE_TIJD, OPROEP} hebben een AO-DOCX; STAGE /
 * PAYROLL vinden geen variant-template (→ TEMPLATE_NOT_FOUND, bewust).
 */
export const CONTRACT_VARIANT = [
  'BEPAALDE_TIJD',
  'ONBEPAALDE_TIJD',
  'OPROEP',
  'STAGE',
  'PAYROLL',
] as const

export const ONBOARDING_DOCUMENT_TYPE_LABELS: Record<OnboardingDocumentType, string> = {
  ARBEIDSOVEREENKOMST:               'Arbeidsovereenkomst',
  LOONHEFFINGSKORTING:               'Loonheffingskorting',
  WELKOMSTBRIEF:                     'Welkomstbrief',
  HANDBOEK_ONTVANGST:                'Ontvangst handboek',
  AVG_PRIVACYVERKLARING:             'AVG / privacyverklaring',
  BRUIKLEEN_VOERTUIG:                'Bruikleenovereenkomst voertuig',
  BRUIKLEEN_KLEDING:                 'Bruikleenovereenkomst kleding',
  BRUIKLEEN_GEREEDSCHAP:             'Bruikleenovereenkomst gereedschap',
  BRUIKLEEN_TELEFOON:                'Bruikleenovereenkomst telefoon',
  ARBEIDSOVEREENKOMST_BEPAALDE_TIJD: 'Arbeidsovereenkomst bepaalde tijd',
  ARBEIDSOVEREENKOMST_0UREN:         'Arbeidsovereenkomst 0-uren',
  OPDRACHTBEVESTIGING:               'Opdrachtbevestiging',
  OVEREENKOMST_VAN_OPDRACHT:         'Overeenkomst van opdracht',
  STUDIEOVEREENKOMST:                'Studieovereenkomst',
  BEDRIJFSAUTO_PRIVEGEBRUIK:         'Bedrijfsauto met privégebruik',
  BEDRIJFSAUTO_VERBOD_PRIVE:         'Bedrijfsauto verbod privégebruik',
  AANBIEDINGSBRIEF:                  'Aanbiedingsbrief',
  AANMELDFORMULIER:                  'Aanmeldformulier (voorblad aanmeldpakket)',
  WELKOMSBRIEF_ONBOARDING:           'Welkomsbrief onboarding',
  OVERIG:                            'Overig',
}

// ─── batch-45 onboarding (FO-382) — welkomsbrief-placeholder-allowlist ────────

/**
 * De vaste placeholder-set voor het WELKOMSBRIEF_ONBOARDING-templatetype
 * (spec 85 §3.3 / §5): dit zijn de ENIGE mergevelden die de welkomsbrief-
 * generatie vult. Er bestaan bewust geen BSN-/loon-mergevelden voor dit type;
 * onbekende placeholders in een geüploade template geven een scan-waarschuwing
 * en renderen leeg (nooit een zichtbaar token-restant).
 *
 *  - Naam    — roepnaam/voornaam van de kandidaat.
 *  - Functie — functionTitle (leeg wanneer onbekend; template blijft leesbaar).
 *  - Link    — exact dezelfde ondertekende welkom-token-URL als in de mail
 *              (één token, aan mail én brief gegeven).
 *  - Bedrijf — BV-naam van de medewerker (vervangt de hardcoded opsomming,
 *              PO-bevestigd spec 85 A2).
 */
export const WELKOMSBRIEF_ONBOARDING_PLACEHOLDERS = [
  'Naam',
  'Functie',
  'Link',
  'Bedrijf',
] as const
export type WelkomsbriefPlaceholder = (typeof WELKOMSBRIEF_ONBOARDING_PLACEHOLDERS)[number]

export const WELKOMSBRIEF_ONBOARDING_PLACEHOLDER_LABELS: Record<WelkomsbriefPlaceholder, string> = {
  Naam:    'Naam van de kandidaat',
  Functie: 'Functie',
  Link:    'Persoonlijke onboarding-link (identiek aan de mail-link)',
  Bedrijf: 'BV-naam (STREEP-entiteit van de medewerker)',
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface OnboardingDocumentDTO {
  id:                  string
  onboardingId:        string
  type:                OnboardingDocumentType
  typeLabel:           string
  titel:               string
  documentId:          string | null
  isGegenereerd:       boolean
  ondertekendOp:       string | null
  ondertekendDoor:     string | null
  ondertekendDoorNaam: string | null
  /**
   * FO-338 §2.2 — is er een bruikbaar DOCX-template gekoppeld voor dit type?
   * False → de UI toont de Genereer-knop uitgelegd-uitgeschakeld met een link
   * naar het templatebeheer (i.p.v. een knop die stil niets doet).
   */
  templateBeschikbaar: boolean
  createdAt:           string
  updatedAt:           string
}

/**
 * FO-338 §2.2 — template-status per document-type in het documenten-blok, zodat
 * de UI vóór het klikken weet welke typen (nog) geen template hebben. `link`
 * wijst naar het templatebeheer (Document Templates).
 */
export interface OnboardingTemplateStatusDTO {
  type: OnboardingDocumentType
  typeLabel: string
  beschikbaar: boolean
}

/** Deep-link naar het beheer van de DOCX-generatietemplates (FO-338 §2.2). */
export const ONBOARDING_TEMPLATE_BEHEER_LINK = '/instellingen/document-templates'

// ─── Status-derived (UI display) ─────────────────────────────────────────────

export type OnboardingDocumentStatus =
  | 'NIET_GEGENEREERD'
  | 'WACHT_OP_HANDTEKENING'
  | 'ONDERTEKEND'

export const ONBOARDING_DOCUMENT_STATUS_LABELS: Record<OnboardingDocumentStatus, string> = {
  NIET_GEGENEREERD:      'Niet gegenereerd',
  WACHT_OP_HANDTEKENING: 'Wacht op handtekening',
  ONDERTEKEND:           'Ondertekend',
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

/**
 * POST `/onboarding/:id/documenten/genereer` — genereer (of hergenereer) een
 * subset documenten op basis van type. Lege `types` = alle ondersteunde
 * types proberen (skip wanneer template ontbreekt — spec §16.4).
 *
 * `force = true` triggert hergenereer-flow (§16.4): nieuwe DocumentVersion,
 * reset `ondertekendOp`. `force = false` slaat reeds bestaande
 * OnboardingDocument-rijen over en returnt ze ongewijzigd.
 *
 * `contractVariant` is een optionele handmatige override voor de
 * ARBEIDSOVEREENKOMST-variant (UI laat HR een afwijkend contracttype kiezen
 * bij hergenereren). Zonder override leidt de generator de variant af uit
 * `employee.contractType`.
 *
 * `assetData` is historisch; voor BRUIKLEEN_VOERTUIG leidt de generator de
 * voertuig-data nu server-side af uit het gekoppelde `Voertuig` (single source
 * of truth), dus dit veld wordt voor voertuigen genegeerd. Overige
 * BRUIKLEEN_*-typen hebben (nog) geen DOCX en gebruiken het ook niet.
 */
export const genereerDocumentenSchema = z.object({
  types:           z.array(z.enum(ONBOARDING_DOCUMENT_TYPE)).optional(),
  force:           z.boolean().default(false),
  contractVariant: z.enum(CONTRACT_VARIANT).optional(),
  assetData: z
    .object({
      kenteken:        z.string().trim().max(20).optional(),
      merkModel:       z.string().trim().max(120).optional(),
      bijtelling:      z.boolean().optional(),
      tankpas:         z.boolean().optional(),
      laadpas:         z.boolean().optional(),
      aantalSleutels:  z.number().int().min(0).max(10).optional(),
    })
    .optional(),
  /** FO-459 (batch-62) - briefpapierkeuze uit de kiezer; leeg = cascade. */
  briefpapier:     BriefpapierKeuzeInvoerSchema.nullable().optional(),
})
export type GenereerDocumentenInput = z.infer<typeof genereerDocumentenSchema>

/**
 * POST `/onboarding/self/documenten/:docId/onderteken` — medewerker klikt
 * "Ik ga akkoord". `akkoord` is verplicht true; de Zod refine garandeert
 * dat we niet per ongeluk een PATCH met andere data accepteren.
 *
 * Optionele `naam` (vrij tekstveld) wordt alleen voor audit-display gebruikt;
 * `ondertekendDoor` blijft de tekenreeks "MEDEWERKER" (spec §5.3) zodat we
 * niet per ongeluk de privé-naam in een server-response lekken.
 */
export const ondertekenDocumentSchema = z.object({
  akkoord: z.literal(true, {
    error: 'Je moet akkoord aanvinken om te ondertekenen',
  }),
  naam:    z.string().trim().min(1).max(200).optional(),
})
export type OndertekenDocumentInput = z.infer<typeof ondertekenDocumentSchema>

// ─── ID-param helpers (used by route handlers) ───────────────────────────────

export const onboardingDocumentIdParamSchema = z.object({
  id:    idParam('id'),
  docId: idParam('docId'),
})
